// Pure data transformation functions extracted from Payslips orchestrator

// Department interface for backend data
export interface Department {
  id: number;
  name: string;
  companyId: number;
  createdOn: string;
  updatedOn: string;
  createdById: number;
  updatedById: number;
  isArchived: boolean;
}

// Employment Status interface for backend data
export interface EmploymentStatus {
  id: number;
  name: string;
  companyId: number;
  createdOn: string;
  updatedOn: string;
  createdById: number;
  updatedById: number;
  isArchived: boolean;
}

// Internal Employee interface for API data
export interface InternalEmployee {
  id: number;
  userId: string;
  name: string;
  employeeId: string;
  department: string | Department;
  departmentId?: number;
  role?: string;
  employmentStatus: string | EmploymentStatus;
  netPay: number;
  payslipStatus: string;
  payslipUrl: string | null;
  basicSalary: number;
  variablePay: number;
  deductions: number;
  adjustments?: number;
  adjustmentAdditions?: number;
  adjustmentDeductions?: number;
  /** FDL 33/2021 Art. 19/20/21 — monetized overtime pay */
  overtimePay?: number;
  overtimeActualWage?: number | null;
  overtimeBasicWage?: number | null;
  regularOvertimeHours?: number;
  premiumOvertimeHours?: number;
  /** GPSSA FL 7/1999 / ADPF AD Law 2/2000 — employee pension contribution */
  pensionDeduction?: number;
  /** KSA SI Law 1421/2000 — GOSI ANI employer share (HR/Compliance only) */
  gosiAniEmployer?: number | null;
  /** KSA SI Law 1421/2000 — GOSI ANI employee share (HR/Compliance only) */
  gosiAniEmployee?: number | null;
  /** KSA SI Law 1421/2000 — GOSI Occupational Hazards employer share (HR/Compliance only) */
  gosiOhEmployer?: number | null;
  /** KSA SI Law 1421/2000 — GOSI SANED employer share (HR/Compliance only) */
  gosiSanedEmployer?: number | null;
  /** KSA SI Law 1421/2000 — GOSI SANED employee share (HR/Compliance only) */
  gosiSanedEmployee?: number | null;
  /** Council of Health Insurance (CCHI) — employee premium share (non-Saudis). */
  cchiEmployeeShare?: number | null;
  /** CO-673 — income tax withheld; part of the row's total deductions, so the list needs it. */
  taxAmount?: number;
  /** Hourly pay §8 — pension / GOSI base (approved hourly earnings); hourly employees only. */
  hourlyStatutoryBase?: number | null;
  includeVariablePay?: boolean;
  includeAdjustmentAdditions?: boolean;
  includeAdjustmentDeductions?: boolean;
  payrollDate: string;
  createdOn: string;
  email: string;
  currencyCode?: string;
}

// Display Employee interface for UI components
export interface DisplayEmployee {
  id: number;
  userId: string;
  name: string;
  employeeId: string;
  department: string;
  departmentId?: number;
  role?: string;
  employmentStatus: string;
  netPay: number;
  payslipStatus: string;
  payslipUrl: string | null;
  basicSalary: number;
  variablePay: number;
  deductions: number;
  adjustments?: number;
  adjustmentAdditions?: number;
  adjustmentDeductions?: number;
  /** FDL 33/2021 Art. 19/20/21 — monetized overtime pay */
  overtimePay?: number;
  overtimeActualWage?: number | null;
  overtimeBasicWage?: number | null;
  regularOvertimeHours?: number;
  premiumOvertimeHours?: number;
  /** GPSSA FL 7/1999 / ADPF AD Law 2/2000 — employee pension contribution */
  pensionDeduction?: number;
  /** KSA SI Law 1421/2000 — GOSI ANI employer share (HR/Compliance only) */
  gosiAniEmployer?: number | null;
  /** KSA SI Law 1421/2000 — GOSI ANI employee share (HR/Compliance only) */
  gosiAniEmployee?: number | null;
  /** KSA SI Law 1421/2000 — GOSI Occupational Hazards employer share (HR/Compliance only) */
  gosiOhEmployer?: number | null;
  /** KSA SI Law 1421/2000 — GOSI SANED employer share (HR/Compliance only) */
  gosiSanedEmployer?: number | null;
  /** KSA SI Law 1421/2000 — GOSI SANED employee share (HR/Compliance only) */
  gosiSanedEmployee?: number | null;
  /** Council of Health Insurance (CCHI) — employee premium share (non-Saudis). */
  cchiEmployeeShare?: number | null;
  /** CO-673 — income tax withheld; part of the row's total deductions, so the list needs it. */
  taxAmount?: number;
  /** Hourly pay §8 — pension / GOSI base (approved hourly earnings); hourly employees only. */
  hourlyStatutoryBase?: number | null;
  includeVariablePay?: boolean;
  includeAdjustmentAdditions?: boolean;
  includeAdjustmentDeductions?: boolean;
  payrollDate: string;
  createdOn: string;
  email: string;
  currencyCode?: string;
}

export interface DepartmentFromAPI {
  id: number;
  name: string;
}

export const getDepartmentName = (dept: string | Department): string => {
  if (!dept) return '';
  if (typeof dept === 'string') return dept;
  if (typeof dept === 'object' && dept.name) return dept.name;
  return String(dept);
};

export const getDepartmentId = (dept: string | Department): number | undefined => {
  if (!dept) return undefined;
  if (typeof dept === 'string') return undefined;
  if (typeof dept === 'object' && dept.id) return dept.id;
  return undefined;
};

export const getEmploymentStatusName = (status: string | EmploymentStatus): string => {
  if (!status) return '';
  if (typeof status === 'string') return status;
  if (typeof status === 'object' && status.name) return status.name;
  return String(status);
};

export const convertToDisplayEmployee = (employee: InternalEmployee): DisplayEmployee => ({
  id: employee.id,
  userId: employee.userId,
  name: employee.name,
  employeeId: employee.employeeId,
  department: getDepartmentName(employee.department),
  departmentId: employee.departmentId ?? getDepartmentId(employee.department),
  role: employee.role,
  employmentStatus: getEmploymentStatusName(employee.employmentStatus),
  netPay: employee.netPay,
  payslipStatus: employee.payslipStatus,
  payslipUrl: employee.payslipUrl,
  basicSalary: employee.basicSalary,
  variablePay: employee.variablePay,
  deductions: employee.deductions,
  adjustments: employee.adjustments,
  adjustmentAdditions: employee.adjustmentAdditions,
  adjustmentDeductions: employee.adjustmentDeductions,
  overtimePay: employee.overtimePay,
  overtimeActualWage: employee.overtimeActualWage,
  overtimeBasicWage: employee.overtimeBasicWage,
  regularOvertimeHours: employee.regularOvertimeHours,
  premiumOvertimeHours: employee.premiumOvertimeHours,
  pensionDeduction: employee.pensionDeduction,
  gosiAniEmployer: employee.gosiAniEmployer,
  gosiAniEmployee: employee.gosiAniEmployee,
  gosiOhEmployer: employee.gosiOhEmployer,
  gosiSanedEmployer: employee.gosiSanedEmployer,
  gosiSanedEmployee: employee.gosiSanedEmployee,
  cchiEmployeeShare: employee.cchiEmployeeShare,
  taxAmount: employee.taxAmount,
  hourlyStatutoryBase: employee.hourlyStatutoryBase,
  includeVariablePay: employee.includeVariablePay,
  includeAdjustmentAdditions: employee.includeAdjustmentAdditions,
  includeAdjustmentDeductions: employee.includeAdjustmentDeductions,
  payrollDate: employee.payrollDate,
  createdOn: employee.createdOn,
  email: employee.email,
  currencyCode: employee.currencyCode,
});

export const getMonthNumber = (monthName: string): number => {
  const months: Record<string, number> = {
    january: 1, february: 2, march: 3, april: 4, may: 5, june: 6,
    july: 7, august: 8, september: 9, october: 10, november: 11, december: 12,
  };
  return months[monthName.toLowerCase()] || new Date().getMonth() + 1;
};

export const getMonthName = (monthNumber: string): string => {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  const idx = parseInt(monthNumber) - 1;
  return months[idx] || `Month ${monthNumber}`;
};

export const mapApiItemToInternalEmployee = (item: any): InternalEmployee => ({
  id: item.id,
  userId: item.userId,
  name: `${item.user?.firstName || ''} ${item.user?.lastName || ''}`.trim() || 'Unknown Employee',
  employeeId: item.user?.employeeId || '',
  department: item.user?.department || '',
  departmentId: item.departmentId ?? (typeof item.user?.department === 'object' ? item.user.department?.id : undefined),
  role: item.user?.role?.name || item.user?.jobTitle || '',
  employmentStatus: item.user?.employmentStatus || '',
  netPay: item.netPay || 0,
  payslipStatus: item.status || 'Pending',
  payslipUrl: item.hasPdf ? 'has-pdf' : null,
  basicSalary: item.basicSalary || 0,
  variablePay: item.variablePay || 0,
  deductions: item.deductions || 0,
  adjustments: item.adjustments || 0,
  adjustmentAdditions: item.adjustmentAdditions || 0,
  adjustmentDeductions: item.adjustmentDeductions || 0,
  overtimePay: item.overtimePay || 0,
  overtimeActualWage: item.overtimeActualWage ?? null,
  overtimeBasicWage: item.overtimeBasicWage ?? null,
  regularOvertimeHours: item.regularOvertimeHours ?? 0,
  premiumOvertimeHours: item.premiumOvertimeHours ?? 0,
  pensionDeduction: item.pensionDeduction || 0,
  gosiAniEmployer: item.gosiAniEmployer ?? null,
  gosiAniEmployee: item.gosiAniEmployee ?? null,
  gosiOhEmployer: item.gosiOhEmployer ?? null,
  gosiSanedEmployer: item.gosiSanedEmployer ?? null,
  gosiSanedEmployee: item.gosiSanedEmployee ?? null,
  cchiEmployeeShare: item.cchiEmployeeShare ?? null,
  taxAmount: item.taxAmount || 0,
  hourlyStatutoryBase: item.hourlyStatutoryBase ?? null,
  includeVariablePay: item.includeVariablePay ?? true,
  includeAdjustmentAdditions: item.includeAdjustmentAdditions ?? true,
  includeAdjustmentDeductions: item.includeAdjustmentDeductions ?? true,
  payrollDate: item.payrollDate || new Date().toISOString(),
  createdOn: item.createdOn || new Date().toISOString(),
  email: item.user?.email || '',
  currencyCode: item.currencyCode || undefined,
});

// The cycle string is also a KEY: Payslips/index.tsx splits it and maps the month name back with
// getMonthNumber (English only). It used the MACHINE locale ('default'), so on a non-English machine the
// month name did not round-trip. Build it from the fixed English list so it is identical everywhere.
const payrollCycleOf = (payrollDate: string): string => {
  const date = new Date(payrollDate);
  return `${getMonthName(String(date.getMonth() + 1))} ${date.getFullYear()}`;
};

export const extractPayrollCycles = (employees: InternalEmployee[]): string[] => {
  return [...new Set(employees.map((e) => payrollCycleOf(e.payrollDate)))].sort((a, b) => {
    const getDate = (str: string) => {
      const [month, year] = str.split(' ');
      return new Date(`${month} 1, ${year}`);
    };
    return getDate(b).getTime() - getDate(a).getTime();
  });
};

export const filterEmployeesByCycle = (employees: DisplayEmployee[], selectedCycle: string): DisplayEmployee[] => {
  if (!selectedCycle) return employees;
  return employees.filter((employee) => {
    return payrollCycleOf(employee.payrollDate) === selectedCycle;
  });
};
